export type VendorModuleType =
  | 'VET'
  | 'WALKER'
  | 'TRAINER'
  | 'CAFE'
  | 'FOOD'
  | 'RESTAURANT'
  | 'PHARMACY'
  | 'LAB'
  | 'CLINIC'
  | 'CREMATION'
  | 'ADOPTION'
  | 'GROOMER';

export type DiscoveryListItem = {
  id: string;
  business_name: string | null;
  description: string | null;
  city: string | null;
  state: string | null;
  module_types: VendorModuleType[];
  services_species: string[];
  avg_rating: number;
  review_count: number;
  is_24x7: boolean;
  cover_photo_path: string | null;
  /**
   * The provider's face, for at-your-door modules (walker, trainer). Null when they never
   * supplied one — every such vendor verified before the photo became mandatory.
   */
  profile_photo_path: string | null;
  dining: DiningCaps | null;
  /**
   * Straight-line km from the `lat`/`lng` sent with the request, to one decimal.
   * Always present now; null means no location was sent or this vendor has no
   * coordinates — NOT zero.
   */
  distance_km: number | null;
};

export type VendorPhotoLite = { id: string; type: string; file_path: string };

export type { PublicOffering } from '@/features/dining/types';
import type { PublicOffering } from '@/features/dining/types';

/** Derived dining capabilities for CAFE/RESTAURANT vendors (null for everyone else). */
export type DiningCaps = {
  kind: 'PET_CAFE' | 'PET_RESTAURANT' | 'PET_BAKERY';
  dine_in: boolean;
  delivery: boolean;
  pickup: boolean;
  walk_in: boolean;
};

export type DiscoveryService = {
  id: string;
  module_type: VendorModuleType;
  name: string;
  description: string | null;
  price_inr: string; // decimal string
  duration_minutes: number | null;
  // LAB tests surface their sample type + fasting flag so the owner sees a fasting reminder.
  lab_sample_type?: import('@/features/lab/types').LabSampleType | null;
  lab_fasting_required?: boolean | null;
  /** LAB / CREMATION: modes the customer can pick (client change, 9 Oct 2026). */
  visit_modes?: import('@/features/vendor-service/types').VisitMode[];
  /** Same value as the vendor's — repeated so a lone service card can show it. */
  distance_km: number | null;
  images: import('@/features/catalog-image/types').CatalogImage[];
  /** This service's own rating (client change, 9 Oct 2026). Absent on non-detail payloads. */
  avg_rating?: number | null;
  review_count?: number;
};

export type DiscoveryListResponse = {
  items: DiscoveryListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type VendorDetail = {
  id: string;
  business_name: string | null;
  description: string | null;
  website_url: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  address: string | null;
  landmark: string | null;
  module_types: VendorModuleType[];
  services_species: string[];
  avg_rating: number;
  review_count: number;
  is_24x7: boolean;
  /** Requires `lat`/`lng` on the detail request; null otherwise. */
  distance_km: number | null;
  dining: DiningCaps | null;
  /** Dining booking offerings (table / entry pass / event); empty for non-dining. */
  offerings: PublicOffering[];
  photos: VendorPhotoLite[];
  /** Cafe/restaurant menu pages, in order (client change, 9 Oct 2026). */
  menu_images: { id: string; file_path: string }[];
  /** The provider's face. Null when they never supplied one — render a fallback. */
  profile_photo_path: string | null;
  /** Keyed by module type; shape varies per module. */
  module_details: Record<string, Record<string, unknown>>;
  /** Active, bookable services (dining offerings excluded — see `offerings`). */
  services: DiscoveryService[];
};

export type DiscoveryFilters = {
  module?: VendorModuleType;
  q?: string;
  city?: string;
  page?: number;
  /** Nearest-first walker ranking (module=WALKER) — browser geolocation coords. */
  lat?: number;
  lng?: number;
  radius_km?: number;
  // CREMATION-only filters.
  home_pickup?: boolean;
  emergency?: boolean;
  cremation_service_type?: import('@/features/cremation/types').CremationServiceType;
  // LAB-only filters.
  home_collection?: boolean;
  lab_sample_type?: import('@/features/lab/types').LabSampleType;
};

/** Modules worth offering in the discovery picker (have a service profile). */
// FOOD & PHARMACY are intentionally absent — they are product-first verticals reached through
// the aggregated Shop (/app/shop), never browsed as vendor cards here.
//
// ADOPTION is absent for a different reason: adoption is peer-to-peer, so pets
// are listed by their owners and browsed at /app/adopt. The server returns
// nothing for module=ADOPTION, so offering it here was a filter that could only
// ever come back empty.
export const DISCOVERY_MODULES: Array<{ value: VendorModuleType; label: string }> = [
  { value: 'VET', label: 'Vet' },
  { value: 'WALKER', label: 'Walker' },
  { value: 'TRAINER', label: 'Trainer' },
  { value: 'GROOMER', label: 'Groomer' },
  { value: 'CAFE', label: 'Café' },
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'CREMATION', label: 'Cremation' },
  { value: 'LAB', label: 'Lab' },
];

/**
 * Display labels for every module a vendor card can list — which is wider than
 * DISCOVERY_MODULES, since a vendor shows all their modules even ones that
 * aren't browsable filters here. Anything missing falls through to the raw
 * SCREAMING_CASE enum on the card.
 */
export const MODULE_LABEL: Record<string, string> = {
  ...Object.fromEntries(DISCOVERY_MODULES.map((m) => [m.value, m.label])),
  LAB: 'Lab',
  CLINIC: 'Clinic',
  FOOD: 'Pet food',
  PHARMACY: 'Pharmacy',
};
